import { z } from "zod";
import { REPORT_SETTINGS_REPORT_KEYS } from "@common/types/reports/report-settings-report-key";
import {
    REPORT_SETTINGS_WATERMARK_MAX_LENGTH,
    getReportSettingsRowVisibilityOptions,
} from "@common/types/reports/report-settings";
import { layoutOwnValuesSchema } from "./layout-setting.schema";
import { numberFormatOwnValuesSchema } from "./number-format-setting.schema";
import { entityReportSettingsOwnerSchema } from "./report-settings-owner.schema";

export const saveReportSettingsReportKeySchema = z.enum(
    REPORT_SETTINGS_REPORT_KEYS,
);

export const SAVE_REPORT_SETTINGS_REPORT_KEYS =
    saveReportSettingsReportKeySchema.options;

const saveReportSettingsFieldsSchema = layoutOwnValuesSchema
    .extend({
        watermarkText: z
            .string()
            .max(REPORT_SETTINGS_WATERMARK_MAX_LENGTH)
            .nullable(),
    })
    .merge(numberFormatOwnValuesSchema)
    .partial();

export const saveReportSettingsNonEmptyFieldsSchema =
    saveReportSettingsFieldsSchema.refine(
        (fields) => Object.keys(fields).length > 0,
        { message: "At least one field must be provided" },
    );

export const refineReportSettingsRowVisibility = (
    {
        reportKey,
        fields,
    }: {
        reportKey: z.infer<typeof saveReportSettingsReportKeySchema>;
        fields: z.infer<typeof saveReportSettingsFieldsSchema>;
    },
    context: z.RefinementCtx,
): void => {
    if (fields.rowVisibility === undefined) {
        return;
    }
    const options = getReportSettingsRowVisibilityOptions(reportKey);
    const isAllowed =
        options !== null &&
        (fields.rowVisibility === null ||
            options.includes(fields.rowVisibility));
    if (!isAllowed) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["fields", "rowVisibility"],
            message: "Account visibility is not available for this report",
        });
    }
};

const saveReportSettingsChangeSchema = z
    .object({
        owner: z.discriminatedUnion("kind", [
            z.object({ kind: z.literal("workspace") }),
            entityReportSettingsOwnerSchema,
        ]),
        reportKey: saveReportSettingsReportKeySchema,
        fields: saveReportSettingsNonEmptyFieldsSchema,
    })
    .superRefine(refineReportSettingsRowVisibility);

const spotKey = ({
    owner,
    reportKey,
}: z.infer<typeof saveReportSettingsChangeSchema>): string =>
    `${owner.kind === "entity" ? owner.entityId : "workspace"}:${reportKey}`;

export const saveWorkspaceReportSettingsBodySchema = z
    .object({
        changes: z.array(saveReportSettingsChangeSchema).min(1),
    })
    .refine(
        ({ changes }) => new Set(changes.map(spotKey)).size === changes.length,
        { message: "Each owner and report key can be changed only once" },
    );

export type SaveReportSettingsChange = z.infer<
    typeof saveReportSettingsChangeSchema
>;
export type SaveWorkspaceReportSettingsBody = z.infer<
    typeof saveWorkspaceReportSettingsBodySchema
>;
